import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { db } from '../firebase';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { GRID_WIDTH, GRID_HEIGHT, MAX_LIVES } from '../constants/game';

// ---------------------------------------------------------------------------
// Module-level helpers
// ---------------------------------------------------------------------------

const getLocalDateStr = () => new Date().toLocaleDateString('en-CA');

const formatCountdown = (diff) => {
  const h = Math.floor((diff / 3600000) % 24);
  const m = Math.floor((diff / 60000)   % 60);
  const s = Math.floor((diff / 1000)    % 60);
  return [h, m, s].map(n => String(n).padStart(2, '0')).join(':');
};

const getNeighborIds = (x, y) => {
  const neighbors = [];
  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      if (dx === 0 && dy === 0) continue;
      neighbors.push(`${x + dx}-${y + dy}`);
    }
  }
  return neighbors;
};

const buildBoard = (dateStr) => {
  const seed = parseInt(dateStr.replace(/-/g, ''), 10);
  const fX   = (seed * 7)  % GRID_WIDTH;
  const fY   = GRID_HEIGHT - 1;

  let path  = [];
  let currX = (seed * 13) % GRID_WIDTH;

  for (let y = 0; y < GRID_HEIGHT; y++) {
    path.push(`${currX}-${y}`);
    const targetX = y === GRID_HEIGHT - 1
      ? fX
      : Math.max(0, Math.min(GRID_WIDTH - 1, currX + ((seed + y * 23) % 5) - 2));
    while (currX !== targetX) {
      currX += targetX > currX ? 1 : -1;
      path.push(`${currX}-${y}`);
    }
  }

  const obstacles = [];
  for (let y = 1; y < GRID_HEIGHT; y++) {
    const count     = ((seed + y * 13) % 4) + 2;
    const available = [];
    for (let x = 0; x < GRID_WIDTH; x++) {
      const id = `${x}-${y}`;
      if (!path.includes(id) && id !== `${fX}-${fY}`) available.push(x);
    }
    for (let i = 0; i < count; i++) {
      if (available.length > 0) {
        const idx = (seed + y + i) % available.length;
        obstacles.push(`${available.splice(idx, 1)[0]}-${y}`);
      }
    }
  }

  return { fishPos: { x: fX, y: fY }, obstacles };
};

const loadSession = (dateStr) => {
  try {
    const saved = localStorage.getItem(`fishle-session-${dateStr}`);
    return saved ? JSON.parse(saved) : null;
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------

export function useGameEngine({ user, stats, updateStats, generateCatch, setHasPlayedToday } = {}) {

  // --- Date ---
  const [todayStr, setTodayStr] = useState(() =>
    window.location.hostname === 'localhost' ? '2026-03-31' : getLocalDateStr()
  );

  // --- Grid State (session-hydrated) ---
  const [cleared, setCleared] = useState(() => loadSession(todayStr)?.cleared ?? []);
  const [flags,   setFlags]   = useState(() => loadSession(todayStr)?.flags   ?? []);
  const [lives,   setLives]   = useState(() => loadSession(todayStr)?.lives   ?? MAX_LIVES);

  // --- Game State ---
  const [gameState,      setGameState]      = useState(() => stats?.history?.[todayStr] ?? 'playing');
  const [isFlaggingMode, setIsFlaggingMode] = useState(false);
  const [showOverlay,    setShowOverlay]    = useState(true);
  const [showHelp,       setShowHelp]       = useState(() => !localStorage.getItem('fishle-seen-help'));
  const [pulsingTiles,   setPulsingTiles]   = useState([]);
  const [activeSnagTile, setActiveSnagTile] = useState(null);
  const [timeLeft,       setTimeLeft]       = useState('');

  // Refs so callbacks always read current values without re-subscribing.
  const todayStrRef          = useRef(todayStr);
  const statsRef             = useRef(stats);
  const userRef              = useRef(user);
  const generateCatchRef     = useRef(generateCatch);
  const updateStatsRef       = useRef(updateStats);
  // Fix 3: setHasPlayedToday through a ref so useCallback deps stay empty
  const setHasPlayedTodayRef = useRef(setHasPlayedToday);

  useEffect(() => { todayStrRef.current          = todayStr;          }, [todayStr]);
  useEffect(() => { statsRef.current             = stats;             }, [stats]);
  useEffect(() => { userRef.current              = user;              }, [user]);
  useEffect(() => { generateCatchRef.current     = generateCatch;     }, [generateCatch]);
  useEffect(() => { updateStatsRef.current       = updateStats;       }, [updateStats]);
  useEffect(() => { setHasPlayedTodayRef.current = setHasPlayedToday; }, [setHasPlayedToday]);

  // --- Board Generation ---
  const { fishPos, obstacles } = useMemo(() => buildBoard(todayStr), [todayStr]);

  // --- Session Persistence ---
  useEffect(() => {
    localStorage.setItem(
      `fishle-session-${todayStr}`,
      JSON.stringify({ cleared, flags, lives })
    );
  }, [cleared, flags, lives, todayStr]);

  // --- Midnight Rollover + Countdown ---
  useEffect(() => {
    const interval = setInterval(() => {
      const now            = new Date();
      const currentDateStr = getLocalDateStr();

      if (window.location.hostname !== 'localhost' && currentDateStr !== todayStrRef.current) {
        setTodayStr(currentDateStr);
        setCleared([]);
        setFlags([]);
        setLives(MAX_LIVES);
        setShowOverlay(true);
        setGameState(statsRef.current?.history?.[currentDateStr] ?? 'playing');
        setHasPlayedTodayRef.current?.(false); 
      }

      const nextMidnight = new Date();
      nextMidnight.setHours(24, 0, 0, 0);
      setTimeLeft(formatCountdown(nextMidnight - now));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // --- Tile Selectability ---
  const getIsSelectable = useCallback((x, y) => {
    const id = `${x}-${y}`;
    if (cleared.includes(id)) return false;
    if (y === 0) return true;
    return [
      { x,     y: y - 1 },
      { x,     y: y + 1 },
      { x: x - 1, y: y },
      { x: x + 1, y: y },
    ].some(n => cleared.includes(`${n.x}-${n.y}`) && !obstacles.includes(`${n.x}-${n.y}`));
  }, [cleared, obstacles]);

  // --- Help Overlay ---
  const closeHelp = useCallback(() => {
    setShowHelp(false);
    localStorage.setItem('fishle-seen-help', 'true');
  }, []);

  // --- Game Completion (Firebase write) ---
  const recordGameCompletion = useCallback(async () => {
    const currentUser = userRef.current;
    if (!currentUser) return;
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        lastPlayedDate: getLocalDateStr(), // 🟢 Standardized string
      });
      setHasPlayedTodayRef.current?.(true);
    } catch (error) {
      console.error('Error recording game completion:', error);
    }
  }, []);

  // --- Record Result ---
  const recordResult = useCallback((result, currentLives) => {
    setGameState(result);
    recordGameCompletion();
    updateStatsRef.current?.(result, todayStrRef.current);

    if (result === 'won') {
      return generateCatchRef.current?.(currentLives);
    }
  }, [recordGameCompletion]);

  // --- Tile Click ---
  const handleTileClick = useCallback((x, y) => {
    if (gameState !== 'playing' || showHelp) return;
    const id = `${x}-${y}`;

    if (isFlaggingMode) {
      if (cleared.includes(id)) return;
      setFlags(prev => prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]);
      return;
    }

    if (!getIsSelectable(x, y) || flags.includes(id)) return;

    const isSnag = obstacles.includes(id);
    const isFish = x === fishPos.x && y === fishPos.y;

    if (isSnag) {
      setActiveSnagTile(id);
      setTimeout(() => setActiveSnagTile(null), 500);
    } else {
      setPulsingTiles(getNeighborIds(x, y));
      setTimeout(() => setPulsingTiles([]), 600);
    }

    setCleared(prev => [...prev, id]);

    if (isSnag) {
      setLives(prev => {
        const next = prev - 1;
        if (next <= 0) recordResult('lost', 0);
        return next;
      });
    } else if (isFish) {
      const fish = recordResult('won', lives);
      return { fish };
    }
  }, [gameState, showHelp, isFlaggingMode, cleared, flags, obstacles, fishPos, getIsSelectable, recordResult, lives]);

  // --- Play Gate ---
  const handleStartFishing = useCallback(async (setView) => {
    if (!userRef.current) {
      setView('game');
      return;
    }
    try {
      const snap = await getDoc(doc(db, 'users', userRef.current.uid));
      if (snap.exists() && snap.data().lastPlayedDate === getLocalDateStr()) {
        setHasPlayedTodayRef.current?.(true);
        alert('You already fished today. Come back tomorrow!');
        return;
      }
      setView('game');
    } catch (error) {
      console.error('Error verifying play status:', error);
    }
  }, []);

  // --- Reset ---
  const resetGameBoard = useCallback(() => {
    setCleared([]);
    setFlags([]);
    setLives(MAX_LIVES);
    setGameState('playing');
  }, []);

  return {
    gridWidth:  GRID_WIDTH,
    gridHeight: GRID_HEIGHT,
    maxLives:   MAX_LIVES,
    todayStr,
    timeLeft,
    handleStartFishing,
    fishPos,
    obstacles,
    cleared,
    flags,
    lives,
    gameState,
    setGameState,
    isFlaggingMode,
    setIsFlaggingMode,
    showOverlay,
    setShowOverlay,
    showHelp,
    setShowHelp,
    closeHelp,
    pulsingTiles,
    activeSnagTile,
    getIsSelectable,
    handleTileClick,
    recordResult,
    resetGameBoard,
  };
}